import { useState } from 'react';
import { takeModalHandoff } from '../../utils/ui/modalHandoff';

function prefersReducedMotionNow() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Receiver side of a modal hand-off — see `src/utils/ui/modalHandoff.js` for
 * the full sender/receiver recipe (read it before wiring this).
 *
 * One-shot per mount: reads (and consumes) whatever was staged under `key`
 * exactly once, synchronously, via a lazy `useState` initializer, so it never
 * re-reads the store on a later re-render of the same mounted component
 * (there would be nothing left anyway — the store entry is one-shot).
 *
 * Reads the OS reduced-motion preference synchronously (a direct
 * `matchMedia` check, not the shared async `useReducedMotion` hook) so the
 * decision is made once, at the same instant the rect is consumed — using
 * the async hook here would risk flipping `morphEnterFromRect` from a rect to
 * `null` a render AFTER the FLIP spring already started.
 *
 * @param {string} key - must match the sender's `stageModalHandoff(key, ...)`
 * @returns {{
 *   morphEnterFromRect: {top:number,left:number,width:number,height:number}|null,
 *   skipEnter: boolean,
 * }} spread directly onto the receiver's `AnimatedModalShell` props
 *   (`morphEnterFromRect`, `skipEnter`). Both are the "nothing staged"
 *   defaults (`null` / `false`) when the TTL lapsed or no sender called
 *   `stageModalHandoff` for this key.
 */
export function useModalHandoffEnter(key) {
  const [state] = useState(() => {
    const rect = takeModalHandoff(key);
    if (!rect) return { morphEnterFromRect: null, skipEnter: false };
    return {
      // Reduced motion nulls the spatial morph but keeps skipEnter — see the
      // "Reduced motion" section in modalHandoff.js for why those don't move
      // together.
      morphEnterFromRect: prefersReducedMotionNow() ? null : rect,
      skipEnter: true,
    };
  });
  return state;
}

export default useModalHandoffEnter;
